<script setup lang="ts">
/**
 * Create or edit one legal document (Terms, Privacy, or any other page an
 * operator adds now that the slug list is dynamic — see `state/legal-slugs.ts`).
 *
 * `row` is `null` in create mode. Editing calls core's `PATCH /admin/legal/:id`
 * for everything except the date, then — only if the operator actually moved
 * the "Last updated" field — a second call to the game-owned
 * `PATCH /admin/cms-ext/legal/:id/updated-at` supplement, because core's own
 * patch has no field for an explicit `updatedAt` (Prisma's `@updatedAt` only
 * auto-stamps when nothing explicit is passed). Creating calls
 * `POST /admin/legal` alone — Prisma sets `updatedAt` to the creation time by
 * default, which is already correct, so there's nothing to supplement.
 */
import { onMounted, ref } from 'vue';

import AdminModal from './AdminModal.vue';
import RichTextEditor from './RichTextEditor.vue';
import { adminApi, type LegalRow } from './admin-api';
import { ApiError } from '@/net/api';

const props = defineProps<{ row: LegalRow | null }>();
const emit = defineEmits<{ close: []; saved: [] }>();

const title = ref(props.row?.title ?? '');
const slug = ref(props.row?.slug ?? '');
const active = ref(props.row?.active ?? false);
const content = ref(props.row?.content ?? '');

/** `datetime-local` wants `YYYY-MM-DDTHH:mm` in the *viewer's* local time, not UTC. */
function toDatetimeLocal(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : new Date();
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const updatedAt = ref(toDatetimeLocal(props.row?.updatedAt));
/** The value the field started at, so Save only sends a date when it actually moved. */
const startingUpdatedAt = updatedAt.value;

const saving = ref(false);
const error = ref<string | null>(null);

onMounted(() => {
  // Nothing to fetch — everything comes from `row`, already in memory.
});

async function save(): Promise<void> {
  const t = title.value.trim();
  const s = slug.value.trim();
  if (!t || !s) {
    error.value = 'Title and slug are both required.';
    return;
  }

  saving.value = true;
  error.value = null;
  try {
    if (props.row) {
      await adminApi.legalPatch(props.row.id, {
        title: t,
        slug: s,
        active: active.value,
        content: content.value,
      });
      if (updatedAt.value !== startingUpdatedAt && updatedAt.value) {
        await adminApi.legalUpdatedAtSet(props.row.id, new Date(updatedAt.value).toISOString());
      }
    } else {
      await adminApi.legalCreate({
        slug: s,
        title: t,
        content: content.value,
        active: active.value,
      });
    }
    emit('saved');
  } catch (caught) {
    error.value = caught instanceof ApiError ? caught.message : 'Could not reach the server.';
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <AdminModal :title="row ? 'Edit legal document' : 'Add legal document'" wide @close="emit('close')">
    <p v-if="error" class="bad">{{ error }}</p>

    <div class="fields">
      <label class="field">
        <span>Title</span>
        <input v-model="title" type="text" data-test="legal-modal-title" />
      </label>
      <label class="field">
        <span>Slug</span>
        <input v-model="slug" type="text" data-test="legal-modal-slug" />
      </label>
      <label class="field">
        <span>State</span>
        <select v-model="active" data-test="legal-modal-state">
          <option :value="true">Published</option>
          <option :value="false">Hidden</option>
        </select>
      </label>
      <label class="field">
        <span>Last updated</span>
        <input v-model="updatedAt" type="datetime-local" data-test="legal-modal-updated-at" />
      </label>
    </div>

    <label class="field" style="margin-top: 0.7rem">
      <span>Description</span>
      <RichTextEditor v-model="content" />
    </label>

    <template #footer>
      <button type="button" @click="emit('close')">Cancel</button>
      <button type="button" class="accent" :disabled="saving" @click="save">
        {{ saving ? 'Saving…' : 'Save' }}
      </button>
    </template>
  </AdminModal>
</template>
